import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Database, FileSpreadsheet, Layers, Code } from 'lucide-react';

export type ModelType = 'SOURCE_TABLE' | 'SOURCE_FILE' | 'STAGING' | 'CUSTOM';

interface AddModelDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreate: (options: { type: ModelType; name: string }) => Promise<void>;
}

export function AddModelDialog({
  open,
  onOpenChange,
  onCreate,
}: AddModelDialogProps) {
  const [modelName, setModelName] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [selectedType, setSelectedType] = useState<ModelType | null>(null);

  // Reset state when dialog opens/closes
  useEffect(() => {
    if (open) {
      setModelName('');
      setSelectedType(null);
      setIsCreating(false);
    }
  }, [open]);

  const handleCreate = async () => {
    if (!selectedType || !modelName.trim()) {
      return;
    }

    setIsCreating(true);
    try {
      await onCreate({ type: selectedType, name: modelName.trim() });
      onOpenChange(false);
    } catch (error) {
      // Error handling is done in the parent component
      console.error('Error creating model:', error);
    } finally {
      setIsCreating(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey && modelName.trim() && selectedType) {
      e.preventDefault();
      handleCreate();
    }
  };

  const getTypeLabel = (type: ModelType) => {
    switch (type) {
      case 'SOURCE_TABLE': return 'Source Table';
      case 'SOURCE_FILE': return 'Source File';
      case 'STAGING': return 'Staging';
      case 'CUSTOM': return 'Custom DBT Model';
    }
  };

  // Step 1: Choose model type
  if (selectedType === null) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Model</DialogTitle>
            <DialogDescription>
              Choose the type of model you want to create.
            </DialogDescription>
          </DialogHeader>
          <DialogBody className="flex flex-col gap-3">
            <Button
              variant="outline"
              className="w-full justify-start h-auto py-3 px-4"
              onClick={() => setSelectedType('SOURCE_TABLE')}
            >
              <Database className="h-5 w-5 mr-3 text-muted-foreground" />
              <div className="text-left">
                <div className="font-medium">Source Table</div>
                <div className="text-sm text-muted-foreground">
                  Connect to an existing database table
                </div>
              </div>
            </Button>
            <Button
              variant="outline"
              className="w-full justify-start h-auto py-3 px-4"
              onClick={() => setSelectedType('SOURCE_FILE')}
            >
              <FileSpreadsheet className="h-5 w-5 mr-3 text-muted-foreground" />
              <div className="text-left">
                <div className="font-medium">Source File</div>
                <div className="text-sm text-muted-foreground">
                  Import data from an uploaded file
                </div>
              </div>
            </Button>
            <Button
              variant="outline"
              className="w-full justify-start h-auto py-3 px-4"
              onClick={() => setSelectedType('STAGING')}
            >
              <Layers className="h-5 w-5 mr-3 text-muted-foreground" />
              <div className="text-left">
                <div className="font-medium">Staging</div>
                <div className="text-sm text-muted-foreground">
                  Create a staging model to transform data
                </div>
              </div>
            </Button>
            <Button
              variant="outline"
              className="w-full justify-start h-auto py-3 px-4"
              onClick={() => setSelectedType('CUSTOM')}
            >
              <Code className="h-5 w-5 mr-3 text-muted-foreground" />
              <div className="text-left">
                <div className="font-medium">Custom DBT Model</div>
                <div className="text-sm text-muted-foreground">
                  Create a custom model with SQL
                </div>
              </div>
            </Button>
          </DialogBody>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  // Step 2: Enter model name
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>New {getTypeLabel(selectedType)}</DialogTitle>
          <DialogDescription>
            Enter a name for your new model.
          </DialogDescription>
        </DialogHeader>
        <DialogBody>
          <Label htmlFor="model-name">Model Name</Label>
          <Input
            id="model-name"
            value={modelName}
            onChange={(e) => setModelName(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Enter model name"
            autoFocus
            disabled={isCreating}
          />
        </DialogBody>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => setSelectedType(null)}
            disabled={isCreating}
          >
            Back
          </Button>
          <Button
            onClick={handleCreate}
            disabled={!modelName.trim() || isCreating}
          >
            {isCreating ? 'Creating...' : 'Create'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
